import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Shield, Rocket, Scale, BookOpen, Mail, Phone, AlertTriangle, Loader2, Sparkles, Send, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface StrategyLibraryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface StrategyStep {
  day: number;
  channel: 'whatsapp' | 'email' | 'sms' | 'protesto' | 'negativacao';
  label: string;
}

interface Strategy {
  id: string;
  icon: typeof Shield;
  title: string;
  description: string;
  color: string;
  steps: StrategyStep[];
}

interface AIGeneratedStrategy {
  title: string;
  description: string;
  steps: StrategyStep[];
}

const strategies: Strategy[] = [
  {
    id: 'preventiva',
    icon: Shield,
    title: 'Cobrança Preventiva',
    description: 'Ideal para manter bons pagadores. Foco em lembretes suaves antes do vencimento.',
    color: 'accent',
    steps: [
      { day: -7, channel: 'email', label: 'Lembrete amigável' },
      { day: -3, channel: 'whatsapp', label: 'Confirmação de vencimento' },
      { day: 0, channel: 'whatsapp', label: 'Dia do vencimento' },
      { day: 1, channel: 'email', label: 'Agradecimento ou reforço' },
    ]
  },
  {
    id: 'turbo',
    icon: Rocket,
    title: 'Recuperação Turbo',
    description: 'Foco em dívidas recentes (até 30 dias). Mix de WhatsApp e SMS para maior alcance.',
    color: 'careful',
    steps: [
      { day: 1, channel: 'whatsapp', label: 'Lembrete imediato' },
      { day: 3, channel: 'sms', label: 'Reforço por SMS' },
      { day: 7, channel: 'whatsapp', label: 'Proposta de acordo' },
      { day: 15, channel: 'email', label: 'Última tentativa amigável' },
      { day: 30, channel: 'whatsapp', label: 'Aviso de medidas' },
    ]
  },
  {
    id: 'ultima',
    icon: Scale,
    title: 'Última Tentativa',
    description: 'Para dívidas antigas. Inclui aviso de protesto e oferta especial de acordo.',
    color: 'destructive',
    steps: [
      { day: 45, channel: 'email', label: 'Oferta final de acordo' },
      { day: 60, channel: 'email', label: 'Aviso de protesto' },
      { day: 75, channel: 'protesto', label: 'Execução do protesto' },
    ]
  }
];

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.963 7.963 0 01-4.066-1.112l-2.827.84.84-2.827A7.963 7.963 0 014 12c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"/>
  </svg>
);

const channelIcons: Record<string, { icon: React.FC<{ className?: string }>; color: string }> = {
  whatsapp: { icon: WhatsAppIcon, color: 'text-[#25d366]' },
  email: { icon: Mail, color: 'text-[hsl(var(--basic-500))]' },
  sms: { icon: Phone, color: 'text-[hsl(var(--careful-500))]' },
  protesto: { icon: AlertTriangle, color: 'text-destructive' },
  negativacao: { icon: XCircle, color: 'text-destructive' },
};

const examplePrompts = [
  "Crie uma régua para clientes jovens que preferem WhatsApp",
  "Estratégia agressiva para dívidas acima de R$ 5.000",
  "Cobrança gentil para clientes idosos com foco em email",
  "Régua para empresas B2B com ciclo de pagamento longo",
];

export function StrategyLibraryModal({ open, onOpenChange }: StrategyLibraryModalProps) {
  const [loadingStrategy, setLoadingStrategy] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('library');
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedStrategy, setGeneratedStrategy] = useState<AIGeneratedStrategy | null>(null);
  const queryClient = useQueryClient();

  const createRuleMutation = useMutation({
    mutationFn: async (strategy: { title: string; description: string; steps: StrategyStep[] }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('Empresa não encontrada');

      const { data: existingRules } = await supabase
        .from('collection_rules')
        .select('prioridade')
        .eq('company_id', profile.company_id)
        .order('prioridade', { ascending: false })
        .limit(1);

      const nextPrioridade = (existingRules?.[0]?.prioridade ?? 0) + 1;

      const { data: newRule, error: ruleError } = await supabase
        .from('collection_rules')
        .insert({
          name: strategy.title,
          company_id: profile.company_id,
          trigger_days: 0,
          channel: 'whatsapp',
          prioridade: nextPrioridade,
          is_active: true
        })
        .select()
        .single();

      if (ruleError) throw ruleError;

      const stepsToInsert = strategy.steps.map((step, index) => ({
        collection_rule_id: newRule.id,
        company_id: profile.company_id as string,
        step_number: index + 1,
        trigger_days: step.day,
        channel: step.channel,
        is_active: true,
        timing_mode: 'relative',
        auto_hour: true
      }));

      const { error: stepsError } = await supabase
        .from('collection_steps')
        .insert(stepsToInsert);

      if (stepsError) throw stepsError;

      return newRule;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-rules'] });
      queryClient.invalidateQueries({ queryKey: ['collection-steps'] });
    }
  });

  const handleUseStrategy = async (strategy: Strategy | AIGeneratedStrategy) => {
    const id = 'id' in strategy ? strategy.id : 'ai-generated';
    setLoadingStrategy(id);
    try {
      await createRuleMutation.mutateAsync(strategy);
      toast.success(`Régua "${strategy.title}" criada com sucesso!`, {
        description: `${strategy.steps.length} comunicações adicionadas.`
      });
      setGeneratedStrategy(null);
      setAiPrompt('');
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating strategy:', error);
      toast.error('Erro ao criar régua', {
        description: 'Não foi possível importar a estratégia.'
      });
    } finally {
      setLoadingStrategy(null);
    }
  };

  const handleGenerateStrategy = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Digite uma descrição para a estratégia');
      return;
    }

    setIsGenerating(true);
    setGeneratedStrategy(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-strategy`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ prompt: aiPrompt }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao gerar estratégia');
      }

      if (data.strategy) {
        setGeneratedStrategy(data.strategy);
        toast.success('Estratégia gerada com sucesso!');
      }
    } catch (error) {
      console.error('Error generating strategy:', error);
      toast.error('Erro ao gerar estratégia', {
        description: error instanceof Error ? error.message : 'Tente novamente'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const getColorClasses = (color: string) => {
    const colorMap: Record<string, { bg: string; border: string; icon: string }> = {
      accent: {
        bg: 'bg-[hsl(var(--accent-50))]',
        border: 'border-[hsl(var(--accent-200))] hover:border-[hsl(var(--accent-400))]',
        icon: 'text-[hsl(var(--accent-600))] bg-[hsl(var(--accent-100))]'
      },
      careful: {
        bg: 'bg-[hsl(var(--careful-50))]',
        border: 'border-[hsl(var(--careful-200))] hover:border-[hsl(var(--careful-400))]',
        icon: 'text-[hsl(var(--careful-600))] bg-[hsl(var(--careful-100))]'
      },
      destructive: {
        bg: 'bg-destructive/5',
        border: 'border-destructive/20 hover:border-destructive/40',
        icon: 'text-destructive bg-destructive/10'
      }
    };
    return colorMap[color] || colorMap.accent;
  };

  const renderStrategyCard = (strategy: Strategy | AIGeneratedStrategy, isAI = false) => {
    const Icon = 'icon' in strategy ? strategy.icon : Sparkles;
    const color = 'color' in strategy ? strategy.color : 'accent';
    const id = 'id' in strategy ? strategy.id : 'ai-generated';
    const colors = getColorClasses(color);
    const isLoading = loadingStrategy === id;

    return (
      <Card 
        key={id}
        className={`transition-all hover-pop cursor-pointer border-2 ${colors.border}`}
      >
        <CardContent className="p-6">
          <div className="flex gap-4">
            <div className={`shrink-0 w-14 h-14 rounded-xl flex items-center justify-center ${colors.icon}`}>
              <Icon className="h-7 w-7" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg text-foreground">
                      {strategy.title}
                    </h3>
                    {isAI && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-gradient-to-r from-[hsl(var(--accent-500))] to-[hsl(var(--basic-500))] text-white">
                        IA
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {strategy.description}
                  </p>
                </div>
                <span className="shrink-0 px-2 py-1 rounded text-xs font-bold uppercase bg-muted text-muted-foreground">
                  {strategy.steps.length} comunicações
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-4">
                {strategy.steps.map((step, idx) => {
                  const ChannelIcon = channelIcons[step.channel]?.icon || Mail;
                  const channelColor = channelIcons[step.channel]?.color || 'text-muted-foreground';
                  
                  return (
                    <div 
                      key={idx}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-background border text-xs"
                      title={step.label}
                    >
                      <span className={`font-mono font-bold ${step.day < 0 ? 'text-[hsl(var(--basic-500))]' : step.day === 0 ? 'text-[hsl(var(--accent-500))]' : step.day < 20 ? 'text-[hsl(var(--careful-500))]' : 'text-destructive'}`}>
                        {step.day > 0 ? `+${step.day}` : step.day === 0 ? '0' : step.day}
                      </span>
                      <ChannelIcon className={`h-4 w-4 ${channelColor}`} />
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 pt-4 border-t flex justify-end">
                <Button
                  onClick={() => handleUseStrategy(strategy)}
                  disabled={isLoading || loadingStrategy !== null}
                  className="gap-2 bg-[hsl(var(--accent-500))] hover:bg-[hsl(var(--accent-600))]"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    'Usar esta Estratégia'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-[hsl(var(--accent-500))]" />
            Biblioteca de Estratégias
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="pt-2">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="library" className="gap-2">
              <BookOpen className="h-4 w-4" />
              Biblioteca
            </TabsTrigger>
            <TabsTrigger value="ai" className="gap-2">
              <Sparkles className="h-4 w-4" />
              Criar com IA
            </TabsTrigger>
          </TabsList>

          <TabsContent value="library" className="mt-4">
            <div className="grid gap-4">
              {strategies.map((strategy) => renderStrategyCard(strategy))}
            </div>
          </TabsContent>

          <TabsContent value="ai" className="mt-4">
            <div className="space-y-4">
              {/* AI Input */}
              <div className="p-4 rounded-xl border-2 border-dashed border-[hsl(var(--accent-200))] bg-[hsl(var(--accent-50))]">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[hsl(var(--accent-500))] to-[hsl(var(--basic-500))] text-white">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-1">
                      Assistente de Estratégias
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Descreva o perfil dos seus clientes ou o tipo de cobrança que deseja e a IA criará uma estratégia personalizada.
                    </p>

                    <div className="flex gap-2">
                      <Input
                        placeholder="Ex: Crie uma régua para clientes que atrasam frequentemente..."
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleGenerateStrategy()}
                        disabled={isGenerating}
                        className="flex-1"
                      />
                      <Button
                        onClick={handleGenerateStrategy}
                        disabled={isGenerating || !aiPrompt.trim()}
                        className="gap-2 bg-gradient-to-r from-[hsl(var(--accent-500))] to-[hsl(var(--basic-500))] hover:opacity-90"
                      >
                        {isGenerating ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                        Gerar
                      </Button>
                    </div>

                    {/* Example prompts */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {examplePrompts.map((prompt, idx) => (
                        <button
                          key={idx}
                          onClick={() => setAiPrompt(prompt)}
                          disabled={isGenerating}
                          className="px-3 py-1 text-xs rounded-full border bg-background hover:bg-muted transition-colors disabled:opacity-50"
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Loading State */}
              {isGenerating && (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Loader2 className="h-10 w-10 animate-spin text-[hsl(var(--accent-500))] mx-auto mb-4" />
                    <p className="text-sm text-muted-foreground">
                      Gerando estratégia personalizada...
                    </p>
                  </div>
                </div>
              )}

              {/* Generated Strategy */}
              {generatedStrategy && !isGenerating && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[hsl(var(--accent-500))]" />
                    <span className="text-sm font-medium text-foreground">
                      Estratégia Gerada
                    </span>
                  </div>
                  {renderStrategyCard(generatedStrategy, true)}
                </div>
              )}

              {/* Empty State */}
              {!generatedStrategy && !isGenerating && (
                <div className="text-center py-8 text-muted-foreground">
                  <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p className="text-sm">
                    Digite uma descrição acima para gerar uma estratégia personalizada com IA
                  </p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
